import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { tierOptions } from '../lanes';
import { orgUnitTypesApi, orgUnitsApi } from '../organizationApi';
import type { OrgUnitTreeNode, OrgUnitTypeOption } from '../organizationTypes';
import { Checkbox } from '../../../../components/inputs/Checkbox';

/**
 * Move one unit to a different tier.
 *
 * Its own form rather than a field on the edit modal, because it is its own operation: the
 * number of records it writes depends on the tree rather than on the form, and a reader
 * changing a name should not be able to move a subtree by accident.
 */
export function MoveTierModal({
  open,
  unit,
  onClose,
  onMoved,
}: {
  open: boolean;
  unit: OrgUnitTreeNode;
  onClose: () => void;
  onMoved: () => void;
}) {
  const [typeId, setTypeId] = useState<string | null>(unit.orgUnitTypeId);
  const [cascade, setCascade] = useState(false);
  const [types, setTypes] = useState<OrgUnitTypeOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    orgUnitTypesApi
      .options(controller.signal)
      .then(setTypes)
      .catch((caught) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        const failure = caught instanceof ApiError ? caught : new ApiError('Could not load the tiers.', 0);

      setError(failure);
      // No focus call: a failed load names no field, so there is nothing to point at.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingTypes(false);
      });

    return () => controller.abort();
  }, []);

  const childCount = unit.children.length;
  const moved = typeId !== null && typeId !== unit.orgUnitTypeId;
  const canSave = moved && confirmed && !saving;

  /*
    The tier it is on now is offered too, and labelled as such — the picker has to be able to
    show where the unit currently sits, or the field reads as empty on the way in.
  */
  const options = tierOptions(types).map((option) =>
    option.value === unit.orgUnitTypeId
      ? { ...option, label: `${option.label} (current)` }
      : option,
  );

  if (typeId && !types.some((t) => t.id === typeId)) {
    options.push({ value: unit.orgUnitTypeId, label: `${unit.orgUnitTypeName} (current)` });
  }

  async function save() {
    if (!canSave || !typeId) return;

    setSaving(true);
    setError(null);

    try {
      await orgUnitsApi.moveTier(unit.id, {
        orgUnitTypeId: typeId,
        cascadeToChildren: cascade,
      });

      onMoved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not move the unit.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, {
        idPrefix: 'org-unit-move-tier',
        fieldIds: { orgUnitTypeId: 'org-unit-move-tier-type' },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Move to tier"
      size="lg"
      id="org-unit-move-tier-modal"
      /*
        Picks a tier the unit is not already on — the form refuses the current one, so
        generating it would produce a payload that cannot save.

        Cascade is left OFF. It is the reader's decision about a whole branch, and a button
        that silently re-ranked every descendant would be a generator making the one choice on
        this form that has consequences beyond the record in front of them.
      */
      autogenerate={{
        onGenerate: () => {
          const others = types.filter((t) => t.id !== unit.orgUnitTypeId);
          if (others.length > 0) setTypeId(others[Math.floor(Math.random() * others.length)].id);
        },
        disabled: saving || types.filter((t) => t.id !== unit.orgUnitTypeId).length === 0,
      }}
      confirm={{
        id: 'org-unit-move-tier-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="org-unit-move-tier-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="org-unit-move-tier-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <p className="text-sm text-slate-600">
          Moving <span className="font-medium text-slate-800">{unit.name}</span>, currently at{' '}
          <span className="font-medium text-slate-800">{unit.orgUnitTypeName}</span>. Its parent
          does not change — only which tier it sits at, and so which lane it draws in.
        </p>

        <Select
          id="org-unit-move-tier-type"
          label="Tier"
          required
          remote
          loading={loadingTypes}
          description="The tier to move to. Managed in Organization settings; a deleted tier cannot be assigned."
          value={typeId}
          onChange={setTypeId}
          options={options}
          // Already ordered by level — see `tierOptions`. Sorting again scrambles the ladder.
          sort="none"
          error={moved ? undefined : 'Choose a different tier.'}
          disabled={saving}
        />

        {/*
          Offered only on a unit that has children.

          Not disabled with a sentence saying there is nothing to carry: the unit cannot gain
          children while this dialog is open, so the control is inapplicable rather than
          temporarily unavailable, and a dead tick explaining itself is noise on a form.

          The consequence is stated before the tick — not appended to the thing the reader is
          already clicking past — and it counts the records either way.
        */}
        {childCount > 0 ? (
          <label
            htmlFor="org-unit-move-tier-cascade"
            className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600"
          >
            <Checkbox
              id="org-unit-move-tier-cascade"
              name="org-unit-move-tier-cascade"
              checked={cascade}
              onChange={(e) => setCascade(e.target.checked)}
              disabled={saving}
            />
            <span>
              <span className="font-medium text-slate-700">Move its child units too</span>
              <br />
              Its {childCount} child unit{childCount === 1 ? '' : 's'} and everything below them
              shift by the same number of tiers, so the branch keeps its shape. Left unticked,
              only this unit moves and its children stay where they are — which can leave a
              child ranked above its own parent.
            </span>
          </label>
        ) : null}
      </div>
    </Modal>
  );
}
